// predecessorLinks.js
//
// Normalize / resolve Task schedule dependency links (FS / SS + lag).

export const LINK_KIND_FS = 'FS';
export const LINK_KIND_SS = 'SS';

const TERMINAL_STATUS_TOKENS = new Set([
  'done',
  'cancelled',
  'canceled',
]);

/**
 * @param {unknown} value
 * @returns {'FS'|'SS'}
 */
export function normalizeLinkKind(value) {
  const raw = String(value || '').trim().toUpperCase();
  return raw === LINK_KIND_SS ? LINK_KIND_SS : LINK_KIND_FS;
}

/**
 * @param {unknown} value
 * @returns {number}
 */
export function normalizeLagDays(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.floor(n));
}

/**
 * @param {unknown} entry
 * @returns {{ taskKey: string, kind: 'FS'|'SS', lagDays: number }|null}
 */
export function normalizePredecessorLink(entry) {
  if (typeof entry === 'string') {
    const taskKey = entry.trim();
    if (!taskKey) return null;
    return { taskKey, kind: LINK_KIND_FS, lagDays: 0 };
  }
  if (!entry || typeof entry !== 'object') return null;

  const taskKey = String(
    entry.taskKey || entry.key || entry.nodeKey || '',
  ).trim();
  if (!taskKey) return null;

  return {
    taskKey,
    kind: normalizeLinkKind(entry.kind || entry.type),
    lagDays: normalizeLagDays(entry.lagDays ?? entry.lag),
  };
}

/**
 * @param {unknown} value
 * @returns {Array<{ taskKey: string, kind: 'FS'|'SS', lagDays: number }>}
 */
export function normalizePredecessorLinks(value) {
  const list = Array.isArray(value) ? value : value == null ? [] : [value];
  const seen = new Set();
  const out = [];

  list.forEach((entry) => {
    const link = normalizePredecessorLink(entry);
    if (!link || seen.has(link.taskKey)) return;
    seen.add(link.taskKey);
    out.push(link);
  });

  return out;
}

/**
 * Presence-aware resolver: prefer predecessorLinks when the key exists on the
 * data section; otherwise adapt legacy predecessors nodelist → FS/0.
 *
 * @param {object|null|undefined} node
 * @param {{ typeKey?: string, dataSection?: string }|null} [options]
 * @returns {Array<{ taskKey: string, kind: 'FS'|'SS', lagDays: number }>}
 */
export function resolveTaskPredecessorLinks(node, options = {}) {
  if (!node || typeof node !== 'object') return [];

  const typeKey = String(options.typeKey || node.type || '').trim();
  const sectionKey = String(
    options.dataSection
    || (typeKey ? `data_${typeKey.toLowerCase()}` : ''),
  ).trim();

  const section = sectionKey && node[sectionKey] && typeof node[sectionKey] === 'object'
    ? node[sectionKey]
    : null;

  if (section && Object.prototype.hasOwnProperty.call(section, 'predecessorLinks')) {
    return normalizePredecessorLinks(section.predecessorLinks);
  }

  // Root-level rich links (rare)
  if (Object.prototype.hasOwnProperty.call(node, 'predecessorLinks')) {
    return normalizePredecessorLinks(node.predecessorLinks);
  }

  // Scan data_* sections for predecessorLinks presence
  const dataKeys = Object.keys(node).filter((key) => key.startsWith('data_'));
  for (const key of dataKeys) {
    const data = node[key];
    if (!data || typeof data !== 'object') continue;
    if (Object.prototype.hasOwnProperty.call(data, 'predecessorLinks')) {
      return normalizePredecessorLinks(data.predecessorLinks);
    }
  }

  // Legacy nodelist
  if (section && Object.prototype.hasOwnProperty.call(section, 'predecessors')) {
    return normalizePredecessorLinks(section.predecessors);
  }
  for (const key of dataKeys) {
    const data = node[key];
    if (!data || typeof data !== 'object') continue;
    if (Object.prototype.hasOwnProperty.call(data, 'predecessors')) {
      return normalizePredecessorLinks(data.predecessors);
    }
  }

  return [];
}

/**
 * @param {unknown} statusValue
 * @returns {boolean}
 */
export function isTerminalTaskStatus(statusValue) {
  if (statusValue == null) return false;
  if (typeof statusValue === 'object') {
    const token = String(
      statusValue.value || statusValue.key || statusValue.label || '',
    ).trim().toLowerCase();
    return TERMINAL_STATUS_TOKENS.has(token);
  }
  return TERMINAL_STATUS_TOKENS.has(String(statusValue).trim().toLowerCase());
}

/**
 * @param {Array<{ taskKey: string, kind: string, lagDays: number }>} linksBySuccessor
 * @param {string} fromKey - proposed predecessor
 * @param {string} toKey - successor receiving the link
 * @returns {boolean} true if adding fromKey → toKey would cycle
 */
export function wouldCreateDependencyCycle(linksBySuccessorKey, fromKey, toKey) {
  const start = String(fromKey || '').trim();
  const target = String(toKey || '').trim();
  if (!start || !target) return false;
  if (start === target) return true;

  const stack = [start];
  const seen = new Set();

  while (stack.length) {
    const current = stack.pop();
    if (!current || seen.has(current)) continue;
    seen.add(current);
    if (current === target) return true;

    const links = linksBySuccessorKey?.[current] || [];
    links.forEach((link) => {
      const pred = String(link?.taskKey || '').trim();
      if (pred) stack.push(pred);
    });
  }

  return false;
}
